import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useTours, type TourId } from '@/stores/tours';

/**
 * v14 guided tour: a spotlight on one thing at a time with a short line on
 * what it does. Steps point at `data-tour="…"` elements; a step whose target
 * isn't on screen (another layout, a feature that's off) is left out. Shown
 * once per device (stores/tours), skippable at any step (Esc too).
 */
export interface TourStep {
  target: string;
  title: string;
  body: string;
}

const PAD = 8;
const GUTTER = 16;
const CARD_W = 320;

/** The first visible element for a target (both layouts can carry the same one). */
export function findTarget(target: string): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>(`[data-tour="${target}"]`);
  for (const el of all) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function rectOf(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
}

export function Tour({ id, steps, when = true, delayMs = 700 }: { id: TourId; steps: TourStep[]; when?: boolean; delayMs?: number }) {
  const seen = useTours((s) => !!s.seen[id]);
  const markSeen = useTours((s) => s.markSeen);
  const reduce = useReducedMotion();
  const [live, setLive] = useState<TourStep[] | null>(null);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  // Start once the screen has settled, with the steps that are on screen.
  useEffect(() => {
    if (!when || seen || live) return;
    const t = window.setTimeout(() => {
      const on = steps.filter((s) => findTarget(s.target));
      if (on.length) {
        setLive(on);
        setI(0);
      }
    }, delayMs);
    return () => window.clearTimeout(t);
  }, [when, seen, live, steps, delayMs]);

  const step = live?.[i] ?? null;
  const measure = useCallback(() => {
    if (!step) return;
    const el = findTarget(step.target);
    setRect(el ? rectOf(el) : null);
  }, [step]);

  useLayoutEffect(() => {
    if (!step) return;
    const el = findTarget(step.target);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) el.scrollIntoView?.({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    }
    measure();
    // Layouts move (sheets slide, cards animate): follow the target.
    const tick = window.setInterval(measure, 250);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.clearInterval(tick);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step, measure, reduce]);

  useEffect(() => {
    if (step) nextRef.current?.focus();
  }, [step]);

  const finish = useCallback(() => {
    markSeen(id);
    setLive(null);
  }, [id, markSeen]);

  useEffect(() => {
    if (!step) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, finish]);

  if (!live || !step || typeof document === 'undefined') return null;
  const last = i === live.length - 1;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(CARD_W, vw - GUTTER * 2);
  // Below the target when there's room, else above; never off screen.
  const below = !rect || rect.top + rect.height + 180 < vh || rect.top < vh / 2;
  const left = rect ? Math.min(Math.max(GUTTER, rect.left + rect.width / 2 - w / 2), vw - w - GUTTER) : (vw - w) / 2;
  const cardStyle = rect
    ? below
      ? { top: Math.min(rect.top + rect.height + 12, vh - 200), left, width: w }
      : { bottom: Math.max(GUTTER, vh - rect.top + 12), left, width: w }
    : { top: vh / 2 - 90, left, width: w };

  return createPortal(
    <div className="fixed inset-0 z-[90]" data-testid={`tour-${id}`} data-step={i}>
      {/* The dim with a hole over the target; clicks on it do nothing (the card has the buttons). */}
      {rect ? (
        <motion.div
          aria-hidden
          className="pointer-events-auto fixed rounded-card"
          initial={false}
          animate={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
          transition={reduce ? { duration: 0 } : { type: 'spring', duration: 0.35, bounce: 0 }}
          style={{ boxShadow: '0 0 0 9999px rgba(10, 10, 10, 0.62)' }}
        />
      ) : (
        <div aria-hidden className="pointer-events-auto fixed inset-0 bg-[rgba(10,10,10,0.62)]" />
      )}
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          role="dialog"
          aria-modal="true"
          aria-labelledby={`tour-${id}-title`}
          aria-describedby={`tour-${id}-body`}
          initial={{ opacity: 0, y: reduce ? 0 : 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, transition: { duration: 0.1 } }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="fixed rounded-card bg-surface p-4 text-fg shadow-lift"
          style={cardStyle}
          data-testid="tour-card"
          data-target={step.target}
        >
          <p className="text-[12px] font-semibold uppercase tracking-wide text-muted">
            {i + 1} of {live.length}
          </p>
          <p id={`tour-${id}-title`} className="mt-1 text-[17px] font-semibold leading-tight">
            {step.title}
          </p>
          <p id={`tour-${id}-body`} className="mt-1 text-[15px] leading-snug text-muted">
            {step.body}
          </p>
          <div className="mt-4 flex items-center gap-2">
            <button type="button" onClick={finish} className="h-9 rounded-full px-2 text-[14px] font-medium text-muted hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg" data-testid="tour-skip">
              Skip tour
            </button>
            <span className="flex-1" />
            {i > 0 && (
              <Button size="sm" variant="subtle" accent="paper" onClick={() => setI((n) => n - 1)} data-testid="tour-back">
                Back
              </Button>
            )}
            <Button ref={nextRef} size="sm" onClick={() => (last ? finish() : setI((n) => n + 1))} data-testid="tour-next">
              {last ? 'Got it' : 'Next'}
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body,
  );
}
